import { supabase } from '@/core/database/supabase';
import type { 
  MaintenanceWorkload, 
  MaintenanceWorkloadItem, 
  WorkloadRule, 
  WorkloadForecast, 
  WorkloadStats, 
  WorkloadFilters 
} from '../types/maintenance-workload.types';

export const MaintenanceWorkloadService = {
  /**
   * Lấy danh sách workload với bộ lọc
   */
  async getWorkloads(companyId: string, filters: WorkloadFilters = {}): Promise<{ data: MaintenanceWorkload[], total: number }> {
    let query = supabase
      .from('maintenance_workloads')
      .select(`
        *,
        project:projects(id, name, code),
        zone:project_zones(id, zone_name, zone_code),
        contract:contracts(id, contract_name, contract_number)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.projectId) query = query.eq('project_id', filters.projectId);
    if (filters.zoneId) query = query.eq('zone_id', filters.zoneId);
    if (filters.status && filters.status !== 'all') query = query.eq('status', filters.status);
    if (filters.search) {
      query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
    }

    const page = filters.page || 1;
    const pageSize = filters.pageSize || 10;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    query = query
      .order('created_at', { ascending: false })
      .range(from, to);

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as MaintenanceWorkload[],
      total: count || 0
    };
  },

  /**
   * Lấy chi tiết workload kèm các item
   */
  async getWorkloadDetail(id: string): Promise<MaintenanceWorkload & { items: MaintenanceWorkloadItem[] }> {
    const { data, error } = await supabase
      .from('maintenance_workloads')
      .select(`
        *,
        project:projects(id, name, code),
        zone:project_zones(id, zone_name, zone_code),
        contract:contracts(id, contract_name, contract_number),
        items:maintenance_workload_items(*)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as any;
  },

  /**
   * Tạo workload mới
   */
  async createWorkload(data: Partial<MaintenanceWorkload>): Promise<MaintenanceWorkload> {
    const { data: result, error } = await supabase
      .from('maintenance_workloads')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return result as MaintenanceWorkload;
  },

  /**
   * Cập nhật workload
   */
  async updateWorkload(id: string, updates: Partial<MaintenanceWorkload>): Promise<MaintenanceWorkload> {
    const { data, error } = await supabase
      .from('maintenance_workloads')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as MaintenanceWorkload;
  },

  /**
   * Xóa workload
   */
  async deleteWorkload(id: string): Promise<void> {
    const { error } = await supabase
      .from('maintenance_workloads')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Lấy danh sách định mức (rules)
   */
  async getWorkloadRules(companyId: string): Promise<WorkloadRule[]> {
    const { data, error } = await supabase
      .from('maintenance_workload_rules')
      .select('*')
      .eq('company_id', companyId);

    if (error) throw error;
    return data as WorkloadRule[];
  },

  /**
   * Tính toán workload tự động dựa trên Asset của Zone
   */
  async calculateWorkload(workloadId: string): Promise<void> {
    // Trong thực tế, đây sẽ là một Edge Function hoặc RPC
    // Ở đây ta gọi RPC giả định
    const { error } = await supabase.rpc('calculate_maintenance_workload', { workload_id: workloadId });
    if (error) throw error;
  },

  /**
   * Lấy thống kê workload
   */
  async getWorkloadStats(companyId: string): Promise<WorkloadStats> {
    const { data, error } = await supabase
      .from('maintenance_workloads')
      .select('status, estimated_man_hours, estimated_material_cost, total_workload_score')
      .eq('company_id', companyId);

    if (error) throw error;

    const stats: WorkloadStats = {
      totalWorkloads: data.length,
      totalManHours: data.reduce((sum, w) => sum + (w.estimated_man_hours || 0), 0),
      totalMaterialCost: data.reduce((sum, w) => sum + (w.estimated_material_cost || 0), 0),
      avgScore: data.length > 0 ? data.reduce((sum, w) => sum + (w.total_workload_score || 0), 0) / data.length : 0,
      byStatus: data.reduce((acc, w) => {
        acc[w.status as any] = (acc[w.status as any] || 0) + 1;
        return acc;
      }, {} as any),
      byType: {} as any // Phụ thuộc vào items, cần query sâu hơn nếu muốn chính xác
    };

    return stats;
  }
};
